import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface CardProps {
  className?: string;
  children: React.ReactNode;
  elevation?: 'low' | 'medium' | 'high';
  hoverEffect?: boolean;
}

const CardContainer = styled(motion.div)<{ 
  $elevation: 'low' | 'medium' | 'high';
  $hoverEffect: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: ${({ theme, $elevation }) => {
    switch ($elevation) {
      case 'low':
        return '0 8px 20px rgba(0, 0, 0, 0.04)';
      case 'medium':
        return '0 12px 28px rgba(0, 0, 0, 0.08)';
      case 'high':
        return '0 16px 36px rgba(0, 0, 0, 0.12)';
      default:
        return '0 8px 20px rgba(0, 0, 0, 0.04)';
    }
  }};
  transition: all 0.4s ease;
  height: 100%;
  position: relative;
  border-top: 4px solid transparent;
  
  ${({ $hoverEffect, theme }) => $hoverEffect && `
    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
      border-top: 4px solid ${theme.colors.accent};
    }
  `}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%);
    pointer-events: none;
  }
`;

const Card: React.FC<CardProps> = ({ 
  className, 
  children, 
  elevation = 'low',
  hoverEffect = true 
}) => {
  return (
    <CardContainer 
      className={className} 
      $elevation={elevation}
      $hoverEffect={hoverEffect}
      whileHover={hoverEffect ? { y: -10 } : undefined}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      {children}
    </CardContainer>
  );
};

export default Card;
